import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import { 
  FaArrowLeft, 
  FaUser, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaEyeSlash,
  FaPlus,
  FaGlobe,
  FaLock,
  FaImage,
  FaCommentDots,
  FaSearch
} from "react-icons/fa";
import { getUserId } from "../utils/userUtils";
import { 
  listUserCharacters, 
  deleteCharacter, 
  toggleCharacterVisibility
} from "../utils/characterUtils";
import axios from "axios";

const CharacterManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthenticator((context) => [context.user]);
  
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [notification, setNotification] = useState(null);
  const [deletingCharacter, setDeletingCharacter] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, public, private

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Check if user has an active subscription
  const checkSubscription = async () => {
    if (!user) {
      setIsSubscribed(false);
      setCheckingSubscription(false);
      return;
    }

    try {
      const userId = getUserId(user);
      if (!userId) {
        setIsSubscribed(false);
        setCheckingSubscription(false);
        return;
      }

      const response = await axios.get(
        `https://d4gscm5o97.execute-api.us-east-1.amazonaws.com/prod/subscriptions/${userId}`
      );

      const subscription = response.data;
      setIsSubscribed(subscription.subscription_status === "active");
    } catch (error) {
      console.error("Error checking subscription:", error);
      setIsSubscribed(false);
    } finally {
      setCheckingSubscription(false);
    }
  };

  // Load user characters
  const loadCharacters = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      const userCharacters = await listUserCharacters(userId);
      setCharacters(userCharacters);
      console.log('Loaded characters:', userCharacters);
    } catch (error) {
      console.error("Error loading characters:", error);
      showNotification("Failed to load characters", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle character deletion
  const handleDeleteCharacter = async (characterId, characterName) => {
    if (!user) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${characterName}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    setDeletingCharacter(characterId);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      await deleteCharacter(characterId, userId);
      
      // Remove character from the list
      setCharacters(prev => prev.filter(char => char.character_id !== characterId));
      showNotification(`"${characterName}" deleted successfully`, "success");
    } catch (error) {
      console.error("Error deleting character:", error);
      showNotification("Failed to delete character", "error");
    } finally {
      setDeletingCharacter(null);
    }
  };

  // Handle visibility toggle
  const handleToggleVisibility = async (characterId, currentVisibility, characterName) => {
    if (!user) return;

    setTogglingVisibility(characterId);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      await toggleCharacterVisibility(characterId, userId, !currentVisibility);
      
      // Update character in the list
      setCharacters(prev => prev.map(char => 
        char.character_id === characterId 
          ? { ...char, is_public: !currentVisibility }
          : char
      ));
      
      const action = !currentVisibility ? "published" : "made private";
      showNotification(`"${characterName}" ${action} successfully`, "success");
    } catch (error) {
      console.error("Error toggling visibility:", error);
      showNotification("Failed to update character visibility", "error");
    } finally {
      setTogglingVisibility(null);
    }
  };

  // Handle opening character chat
  const handleOpenChat = (character) => {
    if (!user) return;
    
    try {
      // Navigate to chat interface with custom character bot_id
      const chatUrl = `/myai?bot_id=${character.character_id}`;
      console.log('Navigating to chat with character:', character.name, 'URL:', chatUrl);
      navigate(chatUrl);
    } catch (error) {
      console.error("Error opening character chat:", error);
      showNotification("Failed to open character chat", "error");
    }
  };

  // Filter characters based on search query and filter type
  const filteredCharacters = characters.filter(character => {
    const matchesSearch = character.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         character.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === "all" || 
                         (filterType === "public" && character.is_public) ||
                         (filterType === "private" && !character.is_public);
    
    return matchesSearch && matchesFilter;
  });

  // Handle cancel - go back to previous page
  const handleCancel = () => {
    navigate(-1);
  };

  useEffect(() => {
    checkSubscription();
    loadCharacters();
  }, [user]);

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center pt-24">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Please sign in to manage characters</h1>
          <button
            onClick={() => navigate("/controlpanel")}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Show subscription requirement if not subscribed
  if (!checkingSubscription && !isSubscribed) {
    return (
      <div className="min-h-screen bg-gray-900 text-white pt-24">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={handleCancel}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <FaArrowLeft size={24} />
            </button>
            <h1 className="text-3xl font-bold">Character Management</h1>
          </div>

          <div className="bg-gray-800 rounded-xl p-8 text-center">
            <FaUser className="mx-auto text-6xl text-gray-600 mb-6" />
            <h2 className="text-2xl font-bold mb-4">Subscription Required</h2>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              Character management is a premium feature. Subscribe to create and manage unlimited custom characters.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate("/subscription-management")}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                View Subscription Plans
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading characters...</p>
        </div>
      </div>
    );
  }

  // Main character management view
  return (
    <div className="min-h-screen bg-gray-900 text-white pt-24">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <FaArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-3xl font-bold">Character Management</h1>
              <p className="text-gray-400">Manage all your custom characters</p>
            </div>
          </div>
          
          <button
            onClick={() => navigate("/create-character")}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <FaPlus size={16} />
            Create New Character
          </button>
        </div>

        {/* Search and Filter Controls */}
        {characters.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search characters by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Filter Dropdown */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-300">Filter:</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-lg text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Characters</option>
                  <option value="public">Public Only</option>
                  <option value="private">Private Only</option>
                </select>
              </div>
            </div>
            
            {/* Results Count */}
            <div className="mt-3 text-sm text-gray-400">
              Showing {filteredCharacters.length} of {characters.length} characters
              {searchQuery && ` matching "${searchQuery}"`}
            </div>
          </div>
        )}

        {/* Characters Grid */}
        {characters.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center">
            <FaUser className="mx-auto text-6xl text-gray-600 mb-6" />
            <h2 className="text-2xl font-bold mb-4">No Characters Yet</h2>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              You haven't created any custom characters yet. Create your first character to get started!
            </p>
            <button
              onClick={() => navigate("/create-character")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2 mx-auto"
            >
              <FaPlus size={16} />
              Create Your First Character
            </button>
          </div>
        ) : filteredCharacters.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center">
            <FaSearch className="mx-auto text-6xl text-gray-600 mb-6" />
            <h2 className="text-2xl font-bold mb-4">No Characters Found</h2>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              {searchQuery 
                ? `No characters match your search for "${searchQuery}". Try adjusting your search terms or filter settings.`
                : `No characters match the current filter. Try changing the filter to "All Characters".`
              }
            </p>
            <div className="flex gap-4 justify-center">
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                >
                  Clear Search
                </button>
              )}
              <button
                onClick={() => setFilterType("all")}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Show All Characters
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCharacters.map((character) => (
              <div key={character.character_id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                {/* Character Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                      {character.avatar_url ? (
                        <img 
                          src={character.avatar_url} 
                          alt={character.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FaUser className="text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white truncate">
                        {character.name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {character.is_public ? (
                          <>
                            <FaGlobe className="text-blue-400" size={12} />
                            <span className="text-blue-400 text-sm">Public</span>
                          </>
                        ) : (
                          <>
                            <FaLock className="text-gray-400" size={12} />
                            <span className="text-gray-400 text-sm">Private</span>
                          </>
                        )}
                        {character.status_emoji && (
                          <span className="text-sm">{character.status_emoji}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Character Description */}
                <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                  {character.description || "No description available"}
                </p>

                {/* Character Stats */}
                <div className="flex items-center space-x-4 mb-4 text-xs text-gray-400">
                  {character.selfie_images && (
                    <div className="flex items-center space-x-1">
                      <FaImage />
                      <span>{character.selfie_images.length} selfies</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <span>Created {new Date(character.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  {/* Primary Actions Row */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleOpenChat(character)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <FaCommentDots size={14} />
                      Chat
                    </button>
                    
                    <button
                      onClick={() => navigate(`/edit-character/${character.character_id}`)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <FaEdit size={14} />
                      Edit
                    </button>
                  </div>
                  
                  {/* Secondary Actions Row */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleVisibility(character.character_id, character.is_public, character.name)}
                      disabled={togglingVisibility === character.character_id}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    >
                      {togglingVisibility === character.character_id ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : character.is_public ? (
                        <>
                          <FaEyeSlash size={14} />
                          Private
                        </>
                      ) : (
                        <>
                          <FaEye size={14} />
                          Publish
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => handleDeleteCharacter(character.character_id, character.name)}
                      disabled={deletingCharacter === character.character_id}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    >
                      {deletingCharacter === character.character_id ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <FaTrash size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {characters.length > 0 && (
          <div className="mt-8 bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{characters.length}</div>
                <div className="text-sm text-gray-400">Total Characters</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {characters.filter(c => c.is_public).length}
                </div>
                <div className="text-sm text-gray-400">Published</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-400">
                  {characters.filter(c => !c.is_public).length}
                </div>
                <div className="text-sm text-gray-400">Private</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {characters.reduce((acc, c) => acc + (c.selfie_images?.length || 0), 0)}
                </div>
                <div className="text-sm text-gray-400">Total Images</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-600 text-white border border-green-500' 
            : 'bg-red-600 text-white border border-red-500'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-3 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterManagement;